<template>
  <EntityList
    v-if="loaded"
    :config="config"
    :custom-query="customQuery"
    :columns="columns"
    :filter-vals="filterVals"
    :filters="filters"
    :formatted-columns="formattedColumns"
    :allow-duplicate="false"
    :export-extra-columns="{
      extraColumns: extraColumns,
      key: 'fields',
      parent: 'field',
    }"
    :extra-actions="extraActions"
    :has-insert-btn="id > 0"
    authority="Request_Type_Management_Submission"
    single-edit
    allow-edit-columns
    desc-sort
    @filtersUpdated="filtersUpdated"
  />
</template>

<script>
import { getUserRights } from '@/auth/utils'
import EntityList from '@/layouts/entity/EntityList.vue'
import store from '@/store'
import storeModule from './storeModule'

export default {
  components: {
    EntityList,
  },
  data() {
    return {
      loaded: false,
      filterVals: [],
      requestTypeOptions: [],
      categoryOptions: [],
      userOptions: [],
      extraColumns: [],
      extraFilters: [],
    }
  },
  computed: {
    id() {
      return this.$route.params.id
    },
    config() {
      return {
        store: storeModule,
        module: 'submissions',
        endpoint: 'submissions',
        route: 'request-submissions',
        insertRoute: { name: `request-submissions-edit`, params: { rid: this.id } },
        title: {
          single: this.$t('Request Type Add'),
          plural: this.$t('Request Types'),
        },
        editRenderCondition: {
          key: 'published',
          value: false,
        },
        deleteRenderCondition: {
          key: 'published',
          value: false,
        },
      }
    },
    columns() {
      return [
        { key: 'id', label: this.$t('id'), sortable: true },
        {
          key: 'requestType.title',
          label: this.$t('Request Type'),
          sortable: true,
        },
        { key: 'user.title', label: this.$t('User'), sortable: false },
        { key: 'creationDate', label: this.$t('Date'), sortable: true },
        { key: 'publishDate', label: this.$t('Publish Date'), sortable: true },
        { key: 'comments', label: this.$t('comments'), visible: false },
        { key: 'actions', label: this.$t('actions') },
      ]
    },
    formattedColumns() {
      return [
        { key: 'creationDate', type: 'date' },
        { key: 'publishDate', type: 'date' },
      ]
    },
    customQuery() {
      if (this.id) {
        return { requestType: this.id }
      }
      return {}
    },
    filters() {
      let filters = [
        {
          name: 'category',
          label: this.$t('Category'),
          options: this.categoryOptions,
        },
      ]
      if (!this.id) {
        filters.push({
          name: 'requestType',
          label: this.$t('Request Type'),
          options: this.requestTypeOptions,
        })
      }
      return [
        ...filters,
        {
          name: 'user',
          label: this.$t('User'),
          options: this.userOptions,
        },
        {
          name: 'startDate',
          type: 'date',
          label: this.$t('Start Date'),
          maxDateFieldIdx: '2',
        },
        {
          name: 'endDate',
          type: 'date',
          label: this.$t('End Date'),
          minDateFieldIdx: '1',
        },
        {
          name: 'published',
          type: 'boolean',
          label: this.$i18n.t('Published'),
        },
        ...this.extraFilters,
      ]
    },
    extraActions() {
      const isManager = getUserRights().includes('Request_Type_Management_Request_Type_Edit')
      return [
        {
          callback: 'getEditLink',
          icon: 'EyeIcon',
          title: 'View',
          renderCondition: {
            key: 'published',
            value: true,
          },
          propertyNotExists: 'finalized',
          authorityValidation: !isManager
        },
        {
          callback: 'getEditLink',
          icon: 'Edit3Icon',
          title: 'Edit',
          variant: 'success',
          renderCondition: {
            key: 'published',
            value: true,
          },
          propertyNotExists: 'finalized',
          authorityValidation: isManager
        },
        {
          callback: 'getEditLink',
          icon: 'EyeIcon',
          title: 'View',
          renderCondition: {
            key: 'finalized',
            value: true,
          },
          authorityValidation: isManager
        },
        {
          endpoint: 'reset',
          icon: 'RotateCcwIcon',
          title: 'Reset',
          renderCondition: {
            key: 'published',
            value: true,
          },
          authorityValidation: isManager
        },
      ]
    },
  },
  async mounted() {
    this.categoryOptions = (await store.dispatch('app-common/fetchRequestTypeCategories')).data
    if (this.id) {
      this.fetchFieldsAndFilters(this.id)
    } else {
      this.requestTypeOptions = (await store.dispatch('app-common/fetchRequestTypes')).data
      this.filterVals[1] = this.requestTypeOptions[0].id
      this.fetchFieldsAndFilters(this.filterVals[1])
    }
    this.userOptions = (await store.dispatch('app-common/fetchUsers', { secondarygroup: 15 })).data
    this.loaded = true
  },
  methods: {
    filtersUpdated(filters) {
      if (!this.id && filters[1]) {
        this.fetchFieldsAndFilters(filters[1])
      }
    },
    fetchFieldsAndFilters(requestType) {
      this.extraFilters = []
      this.fetchExtraFields(requestType)
      store.dispatch('app-common/fetchRequestTypeFieldsDetailedList', { requestType, filter: true })
        .then(response => {
          if (response.data.length) {
            this.extraFilters = response.data.map(field => ({
              name: `field_${field.id}`,
              label: field.title,
              options: field.options.map(title => ({ id: title, title }))
            }))
          }
        })
    },
    fetchExtraFields(requestTypeId) {
      store
        .dispatch('app-common/fetchRequestTypePlainFields', requestTypeId)
        .then(response => {
          this.extraColumns = response.data
        })
    },
  },
}
</script>
